import {
  Button,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel
} from "@prismicio/editor-ui"
import type { GeoPointContent } from "@prismicio/types-internal/lib/content"
import type { GeoPoint } from "@prismicio/types-internal/lib/customtypes"
import { useState } from "react"

import { FieldTextInput } from "../FieldTextInput"
import { DEFAULT_PLACEHOLDER, getFieldLabel } from "../utils"
import { coordinatesToString, parseString } from "./geoPointFieldUtils"

interface GeoPointFieldProps extends useGeoPointFieldProps {
  field: GeoPoint
  id: string
  readOnly: boolean
}

export function GeoPointField(props: GeoPointFieldProps) {
  const { field, id, readOnly, ...hookProps } = props

  const {
    onValueChange,
    displayedValue,
    onBlur,
    clearValue,
    isValidCoordinates
  } = useGeoPointField(hookProps)

  return (
    <Field>
      <FieldIcon name="place" />
      <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
      <FieldControl>
        <FieldTextInput
          id={id}
          onValueChange={onValueChange}
          placeholder={DEFAULT_PLACEHOLDER}
          readOnly={readOnly}
          value={displayedValue}
          onBlur={onBlur}
          variant={isValidCoordinates ? "link" : "normal"}
          endAdornment={
            <Button
              startIcon="close"
              color="grey"
              size="small"
              disabled={!displayedValue}
              onClick={clearValue}
            />
          }
        />
      </FieldControl>
    </Field>
  )
}

interface useGeoPointFieldProps {
  content: GeoPointContent | undefined
  onContentChange: (content?: GeoPointContent) => void
}

function useGeoPointField(props: useGeoPointFieldProps) {
  const { content, onContentChange } = props

  const contentValue = coordinatesToString(content?.position)

  const [displayedValue, setDisplayedValue] = useState(contentValue)

  function onBlur() {
    const coordinates = parseString(displayedValue)

    if (!coordinates) {
      setDisplayedValue("")
      return onContentChange(undefined)
    }

    const { lat, lng } = coordinates

    setDisplayedValue(coordinatesToString({ lat, lng }))
    onContentChange({ __TYPE__: "GeoPointContent", position: { lat, lng } })
  }

  function clearValue() {
    setDisplayedValue("")
    onContentChange(undefined)
  }

  const isValidCoordinates = Boolean(parseString(displayedValue))

  return {
    displayedValue,
    isValidCoordinates,
    onValueChange: setDisplayedValue,
    onBlur,
    clearValue
  }
}
